import {
  Plus,
  Settings,
  Calendar,
  Check,
  CheckSquare,
  UserCheck,
  UserX,
  Pencil,
  Copy,
  Trash2,
  ClipboardCheck,
  AlertTriangle,
  Wallet,
  UserPlus,
  X,
  MapPin,
  StickyNote,
  ExternalLink,
  Sparkles,
  ShieldAlert,
  LayoutGrid,
  List as ListIcon,
  ChevronDown,
  ChevronUp,
  RotateCw,
  SlidersHorizontal,
  MessageSquare,
  Zap,
} from 'lucide-react';
import { useMemo, useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import EmptyState from '../../components/EmptyState';
import { EmptySessions } from '../../components/illustrations';
import { formatNXC } from '../../lib/formatNXC';
import useSessionLifecycle from '../../hooks/useSessionLifecycle';
import useSessionsView from '../../hooks/useSessionsView';
import CountdownChip from '../../components/CountdownChip';
import SessionStatusBadge from '../../components/SessionStatusBadge';
import SessionCardDecoration from '../../components/SessionCardDecoration';
import SessionPollsCard from '../../components/SessionPollsCard';
import LiveRSVPHero from '../../components/LiveRSVPHero';
import SessionRulesCard from '../../components/SessionRulesCard';
import BulkSessionsSheet from '../../components/BulkSessionsSheet';
import FeeReconciliationPanel from '../../components/FeeReconciliationPanel';
import EditAttendeeFeesDialog from '../../components/EditAttendeeFeesDialog';
import EditFinesDialog from '../../components/EditFinesDialog';
import SessionPaymentDialog from '../../components/SessionPaymentDialog';
import SessionsCalendar from '../../components/SessionsCalendar';
import SessionImportSheet from '../../components/SessionImportSheet';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  CalendarRange, CalendarPlus, BarChart3, MoreVertical, RotateCcw, Coins as CoinsIcon, Edit3, Loader2,
  FileSpreadsheet,
} from 'lucide-react';

/** iter115 — short clock-time formatter for RSVP timestamps. "17:00:00". */
function formatRsvpAt(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch { return ''; }
}

export default function AttendanceTab({ ctx }) {
  const {
    isAdmin,
    sessions,
    fineDefaults,
    submitting,
    setShowSessionDefaults,
    openNewSessionWithDefaults,
    openAttendance,
    handleAutoFine,
    handleChargeSessionFee,
    openEditSession,
    openDuplicateSession,
    handleDeleteSession,
    handleRemoveGuest,
    handleRsvp,
    handleLockRsvp,
    openAdminRsvp,
    handleAdminRsvpRemove,
    handleAddGuest,
    handleCancelSession,
    sessionDefaults,
    focusedSessionId,
    group,
    fetchSessions,
    fetchGroup,
    silentGuestMatchByGuestId,
    silentGuestRefresh,
    playEnabled,
    openCourtMode,
  } = ctx;

  // iter197.5 — Sessions tab has two modes: "schedule" (default list) vs
  // "reconcile" (fee tally panel).
  const [viewMode, setViewMode] = useState('schedule');
  const [showBulk, setShowBulk] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showFeeDialog, setShowFeeDialog] = useState(false);
  const [bulkFee, setBulkFee] = useState('');
  const [bulkSkipped, setBulkSkipped] = useState([]);
  const [retryingId, setRetryingId] = useState(null);

  const undoAndRetry = async (row) => {
    setRetryingId(row.id);
    try {
      await api.post(`/sessions/${row.id}/payment/undo`);
      const r = await api.post(`/groups/${group.id}/sessions/bulk-action`, {
        session_ids: [row.id], action: row.action,
      });
      if (r.data.done_count) {
        toast.success(`${row.label} — settled from the group fund`);
        setBulkSkipped((prev) => prev.filter((x) => x.id !== row.id));
      } else {
        toast.error(r.data.skipped?.[0]?.reason || 'Still could not apply it');
      }
      fetchSessions?.();
      fetchGroup?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not undo that payment');
    } finally {
      setRetryingId(null);
    }
  };

  const toggleSelect = (id) => setSelectedIds((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const runBulkAction = async (action, sessionFee) => {
    setBulkBusy(true);
    try {
      const r = await api.post(`/groups/${group.id}/sessions/bulk-action`, {
        session_ids: selectedIds, action, session_fee: sessionFee,
      });
      toast.success(r.data.message);
      setBulkSkipped((r.data.skipped || []).map((x) => ({ ...x, action })));
      setSelectedIds([]);
      setSelectMode(false);
      setShowFeeDialog(false);
      setBulkFee('');
      fetchSessions?.();
      fetchGroup?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not apply that');
    } finally {
      setBulkBusy(false);
    }
  };
  const [showSessionImport, setShowSessionImport] = useState(false);
  // Per-attendee fee edit dialog state
  const [editFeesSessionId, setEditFeesSessionId] = useState(null);
  // iter206 — per-fine edit dialog state.
  const [editFinesSessionId, setEditFinesSessionId] = useState(null);
  // Reverse-fees + re-open confirmations
  const [busySessionId, setBusySessionId] = useState(null);
  // iter227 — Session payment (who paid the venue?) dialog state + venue list cache
  const [paymentSession, setPaymentSession] = useState(null);
  const [venues, setVenues] = useState([]);
  useEffect(() => {
    if (!group?.id) return;
    let cancelled = false;
    api.get(`/groups/${group.id}/venues`).then((r) => {
      if (!cancelled) setVenues(r.data?.venues || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [group?.id]);
  const handleRecordPayment = (session) => setPaymentSession(session);
  const handleUndoPayment = async (sessionId) => {
    if (!window.confirm('Undo the recorded payment for this session? Any reimbursement expense will be reversed.')) return;
    try {
      await api.post(`/sessions/${sessionId}/payment/undo`);
      toast.success('Payment undone');
      fetchSessions?.();
      fetchGroup?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Undo failed');
    }
  };

  // Admin's chosen live-state visual style (red_pulse | green_glow | gradient_banner | minimal).
  const liveStyle = sessionDefaults?.live_indicator_style || 'red_pulse';
  const durationMin = sessionDefaults?.duration_minutes || 120;

  // iter203 — convert a silently-added guest whose phone has now signed up.
  const [convertingGuestId, setConvertingGuestId] = useState(null);
  const handleConvertSilentGuest = async (sessionId, match) => {
    if (!match) return;
    setConvertingGuestId(match.guest_id);
    try {
      const r = await api.post(`/groups/${group.id}/silent-guests/convert`, {
        session_id: sessionId,
        guest_id: match.guest_id,
        user_id: match.matched_user.id,
      });
      toast.success(r.data?.message || `${match.matched_user.name}'s visits linked to their account`);
      await Promise.all([
        silentGuestRefresh?.(),
        fetchSessions?.(),
        fetchGroup?.(),
      ]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Conversion failed');
    } finally {
      setConvertingGuestId(null);
    }
  };

  // Sort: live sessions first, then today + upcoming by start time, then past at
  // the bottom. iter327 — the cut-off is the START OF TODAY, not "now", so a
  // session that already finished this morning still leads the Upcoming list
  // instead of being pushed below next week's.
  const sortedSessions = useMemo(() => {
    if (!sessions) return [];
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return [...sessions].sort((a, b) => {
      const buildStart = (s) => {
        if (!s?.date) return new Date(0);
        const [yy, mo, dd] = s.date.split('-').map((x) => parseInt(x, 10) || 0);
        const [hh, mm] = (s.start_time || '00:00').split(':').map((x) => parseInt(x, 10) || 0);
        return new Date(yy, mo - 1, dd, hh, mm);
      };
      const sa = buildStart(a);
      const sb = buildStart(b);
      const ea = new Date(sa.getTime() + durationMin * 60_000);
      const eb = new Date(sb.getTime() + durationMin * 60_000);
      const liveA = now >= sa && now <= ea && a.status !== 'cancelled' && a.status !== 'completed';
      const liveB = now >= sb && now <= eb && b.status !== 'cancelled' && b.status !== 'completed';
      if (liveA && !liveB) return -1;
      if (liveB && !liveA) return 1;
      // Upcoming before past
      const futureA = sa >= startOfToday;
      const futureB = sb >= startOfToday;
      if (futureA && !futureB) return -1;
      if (futureB && !futureA) return 1;
      // Both upcoming → soonest first. Both past → most recent first.
      return futureA ? sa - sb : sb - sa;
    });
  }, [sessions, durationMin]);

  // iter211 — persisted view preferences (per group)
  const { view, updateView, resetView } = useSessionsView(group?.id);

  // iter211 — Apply tab + hideCancelled + quickRange filters on top of sortedSessions
  const visibleSessions = useMemo(() => {
    if (!sortedSessions || sortedSessions.length === 0) return [];
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfToday.getDate() - startOfToday.getDay()); // Sunday
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const last30 = new Date(startOfToday); last30.setDate(last30.getDate() - 30);

    const buildStart = (s) => {
      if (!s?.date) return new Date(0);
      const [yy, mo, dd] = s.date.split('-').map((x) => parseInt(x, 10) || 0);
      const [hh, mm] = (s.start_time || '00:00').split(':').map((x) => parseInt(x, 10) || 0);
      return new Date(yy, mo - 1, dd, hh, mm);
    };

    return sortedSessions.filter((s) => {
      // Hide cancelled
      if (view.hideCancelled && s.status === 'cancelled') return false;
      const sd = buildStart(s);
      // Tab filter
      if (view.tab === 'upcoming' && sd < startOfToday) return false;
      if (view.tab === 'past' && sd >= startOfToday) return false;
      // Quick range filter
      if (view.quickRange === 'today') {
        const eod = new Date(startOfToday); eod.setDate(eod.getDate() + 1);
        if (sd < startOfToday || sd >= eod) return false;
      } else if (view.quickRange === 'this_week') {
        const eow = new Date(startOfWeek); eow.setDate(eow.getDate() + 7);
        if (sd < startOfWeek || sd >= eow) return false;
      } else if (view.quickRange === 'this_month') {
        const eom = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        if (sd < startOfMonth || sd >= eom) return false;
      } else if (view.quickRange === 'last_30') {
        if (sd < last30 || sd > now) return false;
      }
      return true;
    });
  }, [sortedSessions, view.tab, view.hideCancelled, view.quickRange]);

  // iter211 — Pagination
  const pagedSessions = useMemo(
    () => visibleSessions.slice(0, view.pageSize),
    [visibleSessions, view.pageSize],
  );

  // iter211 — Group-by-month accordion (built from paged list)
  const groupedByMonth = useMemo(() => {
    if (!view.groupByMonth) return null;
    const map = new Map();
    pagedSessions.forEach((s) => {
      const key = (s.date || '').slice(0, 7) || 'unknown';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    });
    // iter327 — nearest month first when looking ahead, most recent first for history
    const asc = view.tab === 'upcoming';
    return Array.from(map.entries()).sort(([a], [b]) => (asc ? a.localeCompare(b) : b.localeCompare(a)));
  }, [pagedSessions, view.groupByMonth, view.tab]);

  // iter211 — collapsed-month set (in-memory only, not persisted)
  const [collapsedMonths, setCollapsedMonths] = useState(new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const toggleMonth = (key) => {
    setCollapsedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const tabCounts = useMemo(() => {
    if (!sortedSessions) return { upcoming: 0, past: 0, all: 0 };
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const buildStart = (s) => {
      if (!s?.date) return new Date(0);
      const [yy, mo, dd] = s.date.split('-').map((x) => parseInt(x, 10) || 0);
      return new Date(yy, mo - 1, dd);
    };
    let upcoming = 0, past = 0;
    sortedSessions.forEach((s) => {
      if (view.hideCancelled && s.status === 'cancelled') return;
      if (buildStart(s) >= startOfToday) upcoming += 1;
      else past += 1;
    });
    const allCount = view.hideCancelled
      ? sortedSessions.filter((s) => s.status !== 'cancelled').length
      : sortedSessions.length;
    return { upcoming, past, all: allCount };
  }, [sortedSessions, view.hideCancelled]);

  return (
    <div data-testid="attendance-tab-body" className="space-y-3">
      {/* iter172 — Live RSVP scoreboard hero (renders only when there's a session with RSVP open) */}
      <LiveRSVPHero ctx={ctx} />
      {bulkSkipped.length > 0 && (
        <div className="rounded-2xl border-2 border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid="bulk-skipped-panel">
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs font-bold text-amber-700 dark:text-amber-400">
              {bulkSkipped.length} session{bulkSkipped.length > 1 ? 's' : ''} left untouched
            </p>
            <button type="button" onClick={() => setBulkSkipped([])} data-testid="bulk-skipped-dismiss"
              className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>
          {bulkSkipped.map((row) => (
            <div key={row.id} className="flex items-center gap-2" data-testid={`bulk-skipped-${row.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate">{row.label}</p>
                <p className="text-xs text-muted-foreground">{row.reason}</p>
              </div>
              {row.reason === 'payment already recorded' && (
                <Button size="sm" variant="outline" className="rounded-full h-8 text-xs shrink-0"
                  disabled={retryingId === row.id} onClick={() => undoAndRetry(row)}
                  data-testid={`bulk-undo-retry-${row.id}`}>
                  {retryingId === row.id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <><RotateCcw className="w-3.5 h-3.5 mr-1" /> Undo payment &amp; retry</>}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {selectMode && selectedIds.length > 0 && (
        <div className="sticky bottom-20 z-30 mx-auto w-full max-w-md rounded-2xl border-2 bg-card shadow-lg p-2.5 space-y-2"
          data-testid="bulk-action-bar">
          <p className="text-xs font-bold">{selectedIds.length} session{selectedIds.length > 1 ? 's' : ''} selected</p>
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" disabled={bulkBusy}
              onClick={() => runBulkAction('pay_group_fund')} data-testid="bulk-pay-fund-btn">
              <Wallet className="w-3.5 h-3.5 mr-1" /> Paid by group fund
            </Button>
            <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" disabled={bulkBusy}
              onClick={() => setShowFeeDialog(true)} data-testid="bulk-change-fee-btn">
              <CoinsIcon className="w-3.5 h-3.5 mr-1" /> Change fee
            </Button>
            <Button size="sm" variant="outline" className="rounded-full h-8 text-xs text-destructive" disabled={bulkBusy}
              onClick={() => {
                if (!window.confirm(`Cancel ${selectedIds.length} session(s)? Sessions that already charged fees are left alone.`)) return;
                runBulkAction('cancel');
              }} data-testid="bulk-cancel-btn">
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
            <Button size="sm" variant="ghost" className="rounded-full h-8 text-xs ml-auto" disabled={bulkBusy}
              onClick={() => setSelectedIds([])} data-testid="bulk-clear-btn">
              Clear
            </Button>
          </div>
        </div>
      )}

      <Dialog open={showFeeDialog} onOpenChange={setShowFeeDialog}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="bulk-fee-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Fee for {selectedIds.length} session{selectedIds.length > 1 ? 's' : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Input type="number" min="0" value={bulkFee} autoFocus placeholder="e.g. 250"
              onChange={(e) => setBulkFee(e.target.value)} data-testid="bulk-fee-input"
              className="h-11 rounded-xl border-2 text-base" />
            <p className="text-xs text-muted-foreground">
              Per player. Leave blank and tap Clear fee to fall back to the group default.
              Sessions whose fees are already charged are skipped.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" className="rounded-full" disabled={bulkBusy}
              onClick={() => runBulkAction('set_fee', -1)} data-testid="bulk-fee-clear">
              Clear fee
            </Button>
            <Button className="rounded-full" disabled={bulkBusy || bulkFee === ''}
              onClick={() => runBulkAction('set_fee', Number(bulkFee))} data-testid="bulk-fee-confirm">
              {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkSessionsSheet
        open={showBulk}
        onClose={() => setShowBulk(false)}
        group={group}
        sessions={sessions}
        sessionDefaults={sessionDefaults}
        fineDefaults={fineDefaults}
        onCreated={() => { fetchSessions?.(); fetchGroup?.(); }}
      />
      <SessionRulesCard groupId={group?.id} isAdmin={isAdmin} onBulkAdd={() => setShowBulk(true)} />
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <h3 className="font-display font-bold">Sessions</h3>
        {isAdmin && (
          <div className="flex gap-2 items-center flex-wrap">
            {/* iter197.5 — Schedule vs Reconcile segmented control */}
            <div className="inline-flex rounded-full border-2 border-border bg-card p-0.5" data-testid="sessions-view-mode-toggle">
              <button
                type="button"
                onClick={() => setViewMode('schedule')}
                className={`px-3 h-7 rounded-full text-xs font-semibold inline-flex items-center gap-1 transition-colors ${
                  viewMode === 'schedule' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="sessions-view-schedule"
              >
                <CalendarRange className="w-3 h-3" /> Schedule
              </button>
              <button
                type="button"
                onClick={() => setViewMode('reconcile')}
                className={`px-3 h-7 rounded-full text-xs font-semibold inline-flex items-center gap-1 transition-colors ${
                  viewMode === 'reconcile' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="sessions-view-reconcile"
              >
                <BarChart3 className="w-3 h-3" /> Reconcile
              </button>
            </div>
            {viewMode === 'schedule' && (
              <>
                <Button size="sm" variant="outline" onClick={() => setShowSessionDefaults(true)} className="rounded-full text-xs" data-testid="session-defaults-btn">
                  <Settings className="w-3.5 h-3.5 mr-1" /> Defaults
                </Button>
                <Button size="sm" variant={selectMode ? 'default' : 'outline'} data-testid="sessions-select-toggle"
                  onClick={() => { setSelectMode((m) => !m); setSelectedIds([]); }} className="rounded-full text-xs">
                  <CheckSquare className="w-3.5 h-3.5 mr-1" /> {selectMode ? 'Done' : 'Select'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowBulk(true)} className="rounded-full text-xs" data-testid="bulk-sessions-btn">
                  <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Book dates
                </Button>
                <Button size="sm" onClick={openNewSessionWithDefaults} className="rounded-full text-xs" data-testid="create-session-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> New Session
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* iter258 — scheduling polls */}
      {viewMode === 'schedule' && (
        <SessionPollsCard groupId={group?.id} isAdmin={isAdmin} myUserId={ctx?.user?.id} />
      )}

      {/* iter312 — backfill past sessions from a spreadsheet (super-admins) */}
      {viewMode === 'schedule' && ctx?.user?.is_super_admin && (
        <button
          type="button"
          onClick={() => setShowSessionImport(true)}
          data-testid="session-import-btn"
          className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-full border-2 border-dashed border-emerald-500/40 text-emerald-700 dark:text-emerald-300 bg-emerald-500/5 hover:bg-emerald-500/10 transition-colors text-xs font-semibold"
        >
          <FileSpreadsheet className="w-3.5 h-3.5" />
          Import past sessions from Excel
        </button>
      )}
      <SessionImportSheet
        open={showSessionImport}
        onOpenChange={setShowSessionImport}
        groupId={group?.id}
        groupName={group?.name}
        onImported={() => { fetchSessions?.(); fetchGroup?.(); }}
      />

      {viewMode === 'reconcile' && isAdmin ? (
        <FeeReconciliationPanel
          groupId={group?.id}
          groupName={group?.name}
          defaultFee={fineDefaults?.session_fee || 0}
        />
      ) : sortedSessions.length === 0 ? (
        <EmptyState
          illustration={EmptySessions}
          title="No sessions yet"
          description={isAdmin ? 'Create a session to track attendance and auto-charge no-shows.' : 'Admins create sessions to track attendance.'}
          testId="no-sessions"
        />
      ) : (() => {
        // iter211 — render a SessionRow with all wired callbacks (DRY for grouped & flat lists)
        const renderRow = (session) => (
          <SessionRow
            key={session.id}
            session={session}
            isAdmin={isAdmin}
            group={group}
            fineDefaults={fineDefaults}
            submitting={submitting}
            liveStyle={liveStyle}
            durationMin={durationMin}
            isFocused={focusedSessionId === session.id}
            playEnabled={playEnabled}
            openCourtMode={openCourtMode}
            openAttendance={openAttendance}
            handleAutoFine={handleAutoFine}
            handleChargeSessionFee={handleChargeSessionFee}
            openEditSession={openEditSession}
            openDuplicateSession={openDuplicateSession}
            handleDeleteSession={handleDeleteSession}
            handleRemoveGuest={handleRemoveGuest}
            handleRsvp={handleRsvp}
            handleLockRsvp={handleLockRsvp}
            openAdminRsvp={openAdminRsvp}
            handleAdminRsvpRemove={handleAdminRsvpRemove}
            handleAddGuest={handleAddGuest}
            handleCancelSession={handleCancelSession}
            onEditFees={(sid) => setEditFeesSessionId(sid)}
            onEditFines={(sid) => setEditFinesSessionId(sid)}
            onReverseFees={async (sid) => {
              if (!window.confirm('Reverse ALL session fees for this session? Each charged attendee will be refunded.')) return;
              setBusySessionId(sid);
              try {
                const r = await api.post(`/sessions/${sid}/reverse-session-fees`);
                toast.success(r.data?.message || 'Reversed');
                fetchSessions?.();
                fetchGroup?.();
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Reverse failed');
              } finally {
                setBusySessionId(null);
              }
            }}
            onReopenSession={async (sid) => {
              if (!window.confirm('Re-open this completed session? Attendance + fines will be cleared so you can re-mark.')) return;
              setBusySessionId(sid);
              try {
                const r = await api.post(`/sessions/${sid}/reopen`);
                toast.success(r.data?.message || 'Re-opened');
                fetchSessions?.();
                fetchGroup?.();
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Re-open failed');
              } finally {
                setBusySessionId(null);
              }
            }}
            isBusy={busySessionId === session.id}
            silentGuestMatchByGuestId={silentGuestMatchByGuestId}
            handleConvertSilentGuest={handleConvertSilentGuest}
            convertingGuestId={convertingGuestId}
            onRecordPayment={handleRecordPayment}
            onUndoPayment={handleUndoPayment}
            venues={venues}
            selectMode={selectMode}
            isSelected={selectedIds.includes(session.id)}
            onToggleSelect={toggleSelect}
          />
        );

        return (
          <div className="space-y-3" data-testid="sessions-list-container">
            {/* iter211 — Filter toolbar */}
            <div className="space-y-2">
              {/* Row 1: tabs + display-mode toggle */}
              <div className="flex items-center gap-2 flex-wrap">
                <div className="inline-flex rounded-full border-2 border-border bg-card p-0.5 flex-1" data-testid="sessions-tab-toggle">
                  {[
                    { id: 'upcoming', label: 'Upcoming', count: tabCounts.upcoming },
                    { id: 'past', label: 'Past', count: tabCounts.past },
                    { id: 'all', label: 'All', count: tabCounts.all },
                  ].map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => updateView({ tab: t.id })}
                      data-testid={`sessions-tab-${t.id}`}
                      className={`flex-1 px-2 h-7 rounded-full text-xs font-semibold transition-colors ${
                        view.tab === t.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {t.label}{' '}
                      <span className={`text-xs ${view.tab === t.id ? 'opacity-80' : 'opacity-60'}`}>
                        ({t.count})
                      </span>
                    </button>
                  ))}
                </div>
                <div className="inline-flex rounded-full border-2 border-border bg-card p-0.5" data-testid="sessions-display-toggle">
                  <button
                    type="button"
                    onClick={() => updateView({ displayMode: 'list' })}
                    data-testid="sessions-display-list"
                    title="List view"
                    className={`px-2 h-7 rounded-full text-xs font-semibold inline-flex items-center transition-colors ${
                      view.displayMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <ListIcon className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => updateView({ displayMode: 'calendar' })}
                    data-testid="sessions-display-calendar"
                    title="Calendar view"
                    className={`px-2 h-7 rounded-full text-xs font-semibold inline-flex items-center transition-colors ${
                      view.displayMode === 'calendar' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <LayoutGrid className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {/* Row 2+3 collapse behind a single Filters button (iter308) */}
              {(() => {
                const activeBits = [
                  view.quickRange !== 'all' && [
                    { id: 'all', label: 'All time' }, { id: 'today', label: 'Today' },
                    { id: 'this_week', label: 'This week' }, { id: 'this_month', label: 'This month' },
                    { id: 'last_30', label: 'Last 30d' },
                  ].find((r) => r.id === view.quickRange)?.label,
                  !view.hideCancelled && 'Cancelled shown',
                  view.displayMode === 'list' && !view.groupByMonth && 'Flat list',
                ].filter(Boolean);
                return (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => setFiltersOpen((v) => !v)}
                        data-testid="sessions-filters-toggle"
                        className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full border-2 text-xs font-bold transition-colors ${
                          activeBits.length ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background text-muted-foreground'
                        }`}
                      >
                        <SlidersHorizontal className="w-3.5 h-3.5" />
                        Filters{activeBits.length ? ` · ${activeBits.length}` : ''}
                      </button>
                      {activeBits.map((b) => (
                        <span key={b} className="px-2.5 h-8 inline-flex items-center rounded-full bg-muted text-xs font-semibold text-muted-foreground" data-testid={`sessions-active-filter-${String(b).toLowerCase().replace(/\s+/g, '-')}`}>
                          {b}
                        </span>
                      ))}
                      {activeBits.length > 0 && (
                        <button
                          type="button"
                          onClick={resetView}
                          title="Reset to default view"
                          data-testid="sessions-reset-view"
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        >
                          <RotateCw className="w-3 h-3" /> Reset
                        </button>
                      )}
                    </div>

                    {filtersOpen && (
                      <div className="rounded-2xl border p-3 space-y-3" data-testid="sessions-filters-panel">
                        <div className="flex items-center gap-1.5 flex-wrap" data-testid="sessions-quick-range">
                          {[
                            { id: 'all', label: 'All time' },
                            { id: 'today', label: 'Today' },
                            { id: 'this_week', label: 'This week' },
                            { id: 'this_month', label: 'This month' },
                            { id: 'last_30', label: 'Last 30d' },
                          ].map((r) => (
                            <button
                              key={r.id}
                              type="button"
                              onClick={() => updateView({ quickRange: r.id })}
                              data-testid={`sessions-range-${r.id}`}
                              className={`px-2.5 h-7 rounded-full text-xs font-semibold border-2 transition-colors ${
                                view.quickRange === r.id
                                  ? 'border-primary bg-primary/10 text-primary'
                                  : 'border-border bg-background text-muted-foreground hover:border-muted-foreground/40'
                              }`}
                            >
                              {r.label}
                            </button>
                          ))}
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-semibold" data-testid="sessions-hide-cancelled-label">
                            <input
                              type="checkbox"
                              checked={view.hideCancelled}
                              onChange={(e) => updateView({ hideCancelled: e.target.checked })}
                              data-testid="sessions-hide-cancelled"
                              className="rounded border-2 accent-primary"
                            />
                            Hide cancelled
                          </label>
                          {view.displayMode === 'list' && (
                            <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-semibold" data-testid="sessions-group-by-month-label">
                              <input
                                type="checkbox"
                                checked={view.groupByMonth}
                                onChange={(e) => updateView({ groupByMonth: e.target.checked })}
                                data-testid="sessions-group-by-month"
                                className="rounded border-2 accent-primary"
                              />
                              Group by month
                            </label>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* iter211 — Content */}
            {visibleSessions.length === 0 ? (
              <EmptyState
                illustration={EmptySessions}
                title="No matching sessions"
                description="Try a different tab, quick range, or untick 'Hide cancelled'."
                testId="no-matching-sessions"
              />
            ) : view.displayMode === 'calendar' ? (
              <SessionsCalendar
                sessions={visibleSessions}
                onSelectSession={(s) => {
                  // For now just open Mark Attendance if eligible, else scroll the row.
                  if (isAdmin) openAttendance?.(s);
                }}
              />
            ) : view.groupByMonth && groupedByMonth ? (
              <div className="space-y-3" data-testid="sessions-grouped-by-month">
                {groupedByMonth.map(([monthKey, rows]) => {
                  const monthLabel = (() => {
                    if (monthKey === 'unknown') return 'No date';
                    const [yy, mo] = monthKey.split('-');
                    const dt = new Date(parseInt(yy, 10), parseInt(mo, 10) - 1, 1);
                    return dt.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
                  })();
                  const collapsed = collapsedMonths.has(monthKey);
                  return (
                    <div key={monthKey} data-testid={`month-group-${monthKey}`} className="space-y-2">
                      <button
                        type="button"
                        onClick={() => toggleMonth(monthKey)}
                        data-testid={`month-toggle-${monthKey}`}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-muted/40 border-2 border-border hover:bg-muted/60 transition-colors"
                      >
                        <span className="font-display font-bold text-sm">
                          {monthLabel} <span className="text-xs text-muted-foreground font-normal">({rows.length})</span>
                        </span>
                        {collapsed ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronUp className="w-4 h-4 text-muted-foreground" />}
                      </button>
                      {!collapsed && (
                        <div className="space-y-3">
                          {rows.map(renderRow)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-3" data-testid="sessions-flat-list">
                {pagedSessions.map(renderRow)}
              </div>
            )}

            {/* iter211 — Load more (pagination) — only in list mode */}
            {view.displayMode === 'list' && visibleSessions.length > view.pageSize && (
              <div className="flex flex-col items-center gap-1 pt-2" data-testid="sessions-pagination">
                <Button
                  variant="outline"
                  onClick={() => updateView({ pageSize: view.pageSize + 50 })}
                  data-testid="sessions-load-more-btn"
                  className="rounded-full text-xs"
                >
                  Load more ({Math.min(50, visibleSessions.length - view.pageSize)} of {visibleSessions.length - view.pageSize} remaining)
                </Button>
                <span className="text-xs text-muted-foreground">
                  Showing {Math.min(view.pageSize, visibleSessions.length)} of {visibleSessions.length}
                </span>
              </div>
            )}
          </div>
        );
      })()}

      {/* iter197.5 — Per-attendee fee edit dialog */}
      <EditAttendeeFeesDialog
        open={Boolean(editFeesSessionId)}
        sessionId={editFeesSessionId}
        onClose={() => setEditFeesSessionId(null)}
        onChange={() => { fetchSessions?.(); fetchGroup?.(); }}
      />
      <EditFinesDialog
        open={Boolean(editFinesSessionId)}
        sessionId={editFinesSessionId}
        onClose={() => setEditFinesSessionId(null)}
        onChange={() => { fetchSessions?.(); fetchGroup?.(); }}
      />
      {/* iter227 — Session payment (who paid the venue?) */}
      <SessionPaymentDialog
        open={!!paymentSession}
        session={paymentSession}
        group={group}
        fineDefaults={fineDefaults}
        venues={venues}
        onClose={() => setPaymentSession(null)}
        onSaved={() => { fetchSessions?.(); fetchGroup?.(); }}
      />
    </div>
  );
}

// iter214 Phase 2b — Self-contained RSVP reminder button. Posts to the
// Phase 2a endpoint, shows a toast with the per-user delivery summary.
function RsvpReminderButton({ session, group }) {
  const [sending, setSending] = useState(false);
  const handleSend = async () => {
    // Count unresponded members for the confirm prompt
    const confirmed = new Set(session.confirmed_ids || []);
    const cancelled = new Set(session.rsvp_out_ids || session.cancelled_ids || []);
    const waitlist = new Set(session.waitlist_ids || []);
    const responded = new Set([...confirmed, ...cancelled, ...waitlist]);
    const memberIds = group?.member_ids || [];
    const pending = memberIds.filter((uid) => !responded.has(uid));
    if (pending.length === 0) {
      toast.info('Everyone has already RSVP\u2019d — nothing to send.');
      return;
    }
    if (!window.confirm(`Send WhatsApp RSVP reminder to ${pending.length} member${pending.length === 1 ? '' : 's'} who haven\u2019t responded yet?`)) return;
    setSending(true);
    const toastId = toast.loading(`Sending ${pending.length} reminder${pending.length === 1 ? '' : 's'}\u2026`);
    try {
      const res = await api.post(`/whatsapp/sessions/${session.id}/send-rsvp-reminder`, {});
      const data = res.data || {};
      if (data.sent === data.total && data.sent > 0) {
        toast.success(`Sent ${data.sent} reminder${data.sent === 1 ? '' : 's'} \u2014 watch the Inbound card for replies.`, { id: toastId });
      } else if (data.sent > 0) {
        toast.warning(`${data.sent}/${data.total} sent, ${data.skipped} skipped \u2014 check Outbound log for details.`, { id: toastId });
      } else if (data.reason === 'no_targets') {
        toast.info('No targets \u2014 everyone has already RSVP\u2019d.', { id: toastId });
      } else {
        toast.error(`Nothing sent (${data.skipped} skipped) \u2014 check Outbound log.`, { id: toastId });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reminder send failed', { id: toastId });
    } finally {
      setSending(false);
    }
  };
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={handleSend}
      disabled={sending}
      data-testid={`send-rsvp-reminder-${session.id}`}
      className="rounded-full text-xs"
      title="Send WhatsApp RSVP reminder with IN/OUT buttons to members who haven\u2019t responded yet"
    >
      <MessageSquare className="w-3.5 h-3.5 mr-1" />
      {sending ? 'Sending\u2026' : 'WA Remind'}
    </Button>
  );
}


function SessionRow({
  session, isAdmin, group, fineDefaults, submitting, liveStyle, durationMin, isFocused,
  openAttendance, handleAutoFine, handleChargeSessionFee, openEditSession,
  openDuplicateSession, handleDeleteSession, handleRemoveGuest, handleRsvp,
  handleLockRsvp, openAdminRsvp, handleAdminRsvpRemove, handleAddGuest, handleCancelSession,
  onEditFees, onEditFines, onReverseFees, onReopenSession, isBusy,
  silentGuestMatchByGuestId, handleConvertSilentGuest, convertingGuestId,
  onRecordPayment, onUndoPayment, venues,
  playEnabled, openCourtMode,
  selectMode, isSelected, onToggleSelect,
}) {
  const lifecycle = useSessionLifecycle(session, { durationMinutes: durationMin });
  const rowRef = useRef(null);

  // Deep-link focus: scroll into view + flash a soft yellow ring for 2.5s.
  useEffect(() => {
    if (!isFocused || !rowRef.current) return;
    rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [isFocused]);
  const isRsvp = !!session.rsvp_opens_at;
  const isOpenForRsvp = session.status === 'rsvp_open' || lifecycle.phase === 'rsvp_open';
  const readyForAttendanceLegacy = session.status === 'open'; // legacy no-RSVP sessions
  void readyForAttendanceLegacy;
  const isCancelled = session.status === 'cancelled';
  const _totalHead = (session.confirmed_ids?.length || 0) + (session.external_guests?.length || 0);
  const needsGuests = session.status === 'needs_guests' && _totalHead < (session.min_players || 0);
  const displayStatus = (session.status === 'needs_guests' && !needsGuests)
    ? 'rsvp_closed'
    : session.status;
  // iter317 — being short of the minimum is a nudge, not a lock. Admins must still
  // be able to mark attendance (and so charge fees and issue fines) for a session
  // that went ahead with fewer players than the minimum.
  const readyForAttendance = displayStatus === 'rsvp_closed' || displayStatus === 'open'
    || displayStatus === 'needs_guests';
  const confirmedCount = session.confirmed_ids?.length || 0;
  const guestCount = session.external_guests?.length || 0;
  const totalHeadcount = confirmedCount + guestCount;

  // Friendly upcoming countdown / "RSVP opens in …" — uses adaptive
  // formatCountdown + final-minute drama styling (iter112 + iter113).
  const upcomingChip = (() => {
    if (!lifecycle.isUpcoming) return null;
    if (lifecycle.phase === 'scheduled' && lifecycle.secondsToRsvpOpen != null) {
      return (
        <CountdownChip
          seconds={lifecycle.secondsToRsvpOpen}
          prefix="RSVP opens in"
          testId={`session-countdown-rsvp-${session.id}`}
        />
      );
    }
    if (lifecycle.secondsToStart != null && lifecycle.secondsToStart > 0) {
      return (
        <CountdownChip
          seconds={lifecycle.secondsToStart}
          prefix="Starts in"
          testId={`session-countdown-start-${session.id}`}
        />
      );
    }
    return null;
  })();

  return (
    <div ref={rowRef} className={isFocused ? 'rounded-2xl ring-4 ring-yellow-400/70 ring-offset-2 ring-offset-background transition-all' : ''} data-focused={isFocused ? 'true' : undefined} data-testid={isFocused ? 'session-focused-row' : 'session-row'}>
    <SessionCardDecoration lifecycle={lifecycle} style={liveStyle}>
      <Card className={`p-4 border-0 rounded-2xl ${isCancelled ? 'opacity-60' : ''} ${lifecycle.isLive ? 'bg-red-50/40 dark:bg-red-950/20' : ''}`} data-testid={`session-${session.id}`}>
        <div className="space-y-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {selectMode && (
                <button type="button" onClick={() => onToggleSelect?.(session.id)}
                  data-testid={`select-session-${session.id}`}
                  className={`w-5 h-5 rounded-md border-2 shrink-0 flex items-center justify-center transition-colors ${isSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40'}`}>
                  {isSelected && <Check className="w-3.5 h-3.5" />}
                </button>
              )}
              <h4 className="font-semibold">{session.title}</h4>
              <SessionStatusBadge lifecycle={lifecycle} />
              {/* iter258 — cost preview before RSVP */}
              {(fineDefaults?.session_fee || 0) > 0 && (lifecycle.isUpcoming || lifecycle.isLive) && !session.fees_charged && !isCancelled && (
                <span
                  className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-bold tabular-nums"
                  data-testid={`session-cost-preview-${session.id}`}
                  title="Estimated session fee per player"
                >
                  ≈ {formatNXC(fineDefaults.session_fee)}/player
                </span>
              )}
              {session.my_rsvp === 'confirmed' && session.my_position && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/15 text-green-700 font-semibold" data-testid={`my-rsvp-chip-${session.id}`}>
                  You: #{session.my_position}/{session.my_position_total} ✓
                </span>
              )}
              {session.my_rsvp === 'waitlist' && session.my_position && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-700 font-semibold" data-testid={`my-rsvp-chip-${session.id}`}>
                  Waitlist #{session.my_position}/{session.my_position_total}
                </span>
              )}
              {session.num_courts > 1 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-muted font-semibold uppercase">
                  {session.num_courts} courts
                </span>
              )}
              {session.fines_issued && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-600 font-semibold uppercase">
                  Fined
                </span>
              )}
              {session.fees_charged && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary font-semibold uppercase" data-testid={`session-fees-charged-${session.id}`}>
                  Fees ✓
                </span>
              )}
              {/* iter227 — payment status badge (post-fees). Pending = admin needs to record who paid the venue. */}
              {(session.payment_status === 'recorded' || (session.payment_status === 'not_needed' && session.payment?.paid_by === 'group_fund')) && session.payment && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold uppercase ${session.payment.paid_by === 'complimentary' ? 'bg-violet-500/15 text-violet-700' : 'bg-emerald-500/15 text-emerald-700'}`} title={session.payment.paid_by === 'complimentary' ? 'Complimentary — free court, no fees charged' : `Paid by ${session.payment.paid_by_name}${session.payment.amount ? ` · ₹${Math.round(session.payment.amount).toLocaleString('en-IN')}` : ''}`} data-testid={`session-paid-${session.id}`}>
                  {session.payment.paid_by === 'complimentary' ? '🎁 Free' : session.payment.paid_by === 'group_fund' ? '$ Fund' : `$ ${session.payment.paid_by_name}`}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              <Calendar className="w-3.5 h-3.5 inline mr-1" />
              {new Date(session.date).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}
              {session.start_time && <> · {session.start_time}</>}
            </p>
            {upcomingChip && (
              <p className="text-xs text-muted-foreground mt-0.5" data-testid={`session-helper-${session.id}`}>
                ⏱ {upcomingChip}
              </p>
            )}
            {lifecycle.phase === 'ended' && session.status !== 'completed' && (
              <p className="text-xs text-amber-700 dark:text-amber-300 font-semibold mt-0.5" data-testid={`session-helper-ended-${session.id}`}>
                ⚠ Session ended — {isAdmin ? 'mark attendance to close out' : 'awaiting admin to mark attendance'}
              </p>
            )}
            {session.status === 'completed' && (
              <div className="flex gap-4 mt-2">
                <span className="text-xs flex items-center gap-1 text-green-600">
                  <UserCheck className="w-3.5 h-3.5" />
                  {session.present_names?.length || 0} present
                </span>
                <span className="text-xs flex items-center gap-1 text-red-600">
                  <UserX className="w-3.5 h-3.5" />
                  {session.absent_names?.length || 0} absent
                </span>
              </div>
            )}
            {session.status === 'completed' && session.absent_names?.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Absent: {session.absent_names.join(', ')}
              </p>
            )}
          </div>
          {playEnabled && !isCancelled && session.date <= new Date(Date.now() + (330 + 1440) * 60000).toISOString().slice(0, 10) && (
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={() => openCourtMode(session.id)} data-testid={`court-mode-${session.id}`}
                className="rounded-full text-xs font-bold text-amber-800 dark:text-amber-200 border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20"
                title="Shuffle balanced doubles teams and log match scores for the leaderboard.">
                <Zap className="w-3.5 h-3.5 mr-1 text-amber-500" /> Court Mode
              </Button>
            </div>
          )}
          {isAdmin && (
            <div className="flex gap-2 flex-wrap justify-end">
              {/* iter214 Phase 2b — WA RSVP reminder for upcoming RSVP sessions */}
              {isRsvp && !isCancelled && (lifecycle.phase === 'rsvp_open' || session.status === 'rsvp_open' || session.status === 'rsvp_locked' || session.status === 'needs_guests' || session.status === 'open') && (
                <RsvpReminderButton session={session} group={group} />
              )}
              {readyForAttendance && (
                <Button size="sm" onClick={() => openAttendance(session)} data-testid={`mark-attendance-${session.id}`} className="rounded-full text-xs" title="Tick who showed up. Absentees become eligible for no-show fines.">
                  <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Mark Attendance
                </Button>
              )}
              {session.status === 'completed' && !session.fines_issued && session.absent_ids?.length > 0 && (
                <Button size="sm" variant="destructive" onClick={() => handleAutoFine(session.id)} disabled={submitting} data-testid={`auto-fine-${session.id}`} className="rounded-full text-xs">
                  <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Auto-Fine ({session.absent_ids.length})
                </Button>
              )}
              {session.status === 'completed' && !session.fees_charged && !session.fees_waived && session.fees_pending_approval && (
                <span className="inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30" data-testid={`fees-pending-approval-${session.id}`} title="Session fees are recorded but waiting for a second admin to co-approve in the treasury queue. No balances have moved yet.">
                  <ShieldAlert className="w-3.5 h-3.5" /> Fees awaiting co-approval
                </span>
              )}
              {session.status === 'completed' && !session.fees_charged && !session.fees_waived && !session.fees_pending_approval && session.present_ids?.length > 0 && (fineDefaults.session_fee || 0) > 0 && (
                <Button size="sm" onClick={() => handleChargeSessionFee(session.id)} disabled={submitting} data-testid={`charge-fee-${session.id}`} className="rounded-full text-xs">
                  <Wallet className="w-3.5 h-3.5 mr-1" /> Charge {formatNXC(fineDefaults.session_fee)} × {session.present_ids.filter((id) => !String(id).startsWith('guest_')).length}
                </Button>
              )}
              {/* iter287 — never hide charging silently: explain WHY it's unavailable */}
              {session.status === 'completed' && !session.fees_charged && !session.fees_waived && !session.fees_pending_approval && !((fineDefaults.session_fee || 0) > 0) && (
                <span className="inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30" data-testid={`charge-unavailable-nofee-${session.id}`} title="The group's per-session fee is 0 or not set, so there's nothing to charge. Set it in Settings → Fines & fees.">
                  <Wallet className="w-3.5 h-3.5" /> Set session fee in Settings to charge
                </span>
              )}
              {session.status === 'completed' && !session.fees_charged && !session.fees_waived && !session.fees_pending_approval && (fineDefaults.session_fee || 0) > 0 && !(session.present_ids?.length > 0) && (
                <span className="inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-semibold bg-muted text-muted-foreground border" data-testid={`charge-unavailable-nopresent-${session.id}`} title="Attendance was saved with nobody marked present, so there is no one to charge. Re-mark attendance to fix.">
                  <Wallet className="w-3.5 h-3.5" /> No one marked present — nothing to charge
                </span>
              )}
              {/* iter197.5 — Completed-session recovery kebab (admin only, after fees charged) */}
              {session.status === 'completed' && (
                <SessionRecoveryKebab
                  session={session}
                  isBusy={isBusy}
                  onEditFees={() => onEditFees?.(session.id)}
                  onEditFines={() => onEditFines?.(session.id)}
                  onReverseFees={() => onReverseFees?.(session.id)}
                  onReopenSession={() => onReopenSession?.(session.id)}
                />
              )}
              <Button size="sm" variant="ghost" onClick={() => openEditSession(session)} data-testid={`edit-session-${session.id}`} className="rounded-full text-xs text-muted-foreground hover:text-primary" title="Edit">
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => openDuplicateSession(session)} data-testid={`duplicate-session-${session.id}`} className="rounded-full text-xs text-muted-foreground hover:text-primary" title="Duplicate">
                <Copy className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => handleDeleteSession(session.id)} data-testid={`delete-session-${session.id}`} className="rounded-full text-xs text-red-500 hover:text-red-600">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>

        {/* iter228 — Session-close checklist. Only shown to admin on sessions
             where attendance has been closed. Gives a one-liner "you're not done"
             cue so admins never forget the reimbursement step. */}
        {isAdmin && (session.status === 'completed' || session.fees_charged) && (
          <div className="mt-2 flex items-center gap-3 text-xs uppercase font-semibold tracking-wide" data-testid={`session-checklist-${session.id}`}>
            <span className="text-emerald-600">✓ Attendance</span>
            <span className={session.fees_charged || session.fees_waived ? 'text-emerald-600' : 'text-muted-foreground'}>
              {session.fees_waived ? '✓ Fees waived (free)' : session.fees_charged ? '✓ Fees' : '· Fees pending'}
            </span>
            <span className={
              session.payment_status === 'recorded' ? 'text-emerald-600' :
              session.payment_status === 'not_needed' ? 'text-emerald-600' :
              'text-amber-600'
            }>
              {session.payment_status === 'recorded' ? '✓ Payer' :
               session.payment_status === 'not_needed'
                 ? (session.payment?.paid_by === 'group_fund' ? '✓ Group fund paid' : '· No venue cash')
                 : '⚠ Payer'}
            </span>
          </div>
        )}

        {/* iter227 — "Who paid the venue?" banner for completed sessions.
             iter275 — also shown on upcoming/live sessions (softer copy) since
             courts are usually pre-paid; backend has no status gate. */}
        {isAdmin && session.payment_status === 'pending' && !isCancelled && (
          (session.status === 'completed' || session.fees_charged) ? (
          <div
            className="mt-3 flex items-center justify-between gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl p-2.5"
            data-testid={`session-payment-banner-${session.id}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Wallet className="w-4 h-4 text-amber-600 shrink-0" />
              <p className="text-xs sm:text-sm text-amber-800 dark:text-amber-300">
                <span className="font-semibold">Record who paid the venue.</span>{' '}
                <span className="text-muted-foreground">Reimburses the payer from the fund if a member fronted the cash.</span>
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => onRecordPayment?.(session)}
              data-testid={`session-record-payment-${session.id}`}
              className="rounded-full text-xs shrink-0"
            >
              Record
            </Button>
          </div>
          ) : (
          <div
            className="mt-3 flex items-center justify-between gap-3 bg-sky-500/10 border border-sky-500/30 rounded-xl p-2.5"
            data-testid={`session-payment-banner-${session.id}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Wallet className="w-4 h-4 text-sky-600 shrink-0" />
              <p className="text-xs sm:text-sm text-sky-800 dark:text-sky-300">
                <span className="font-semibold">Pre-paid the venue?</span>{' '}
                <span className="text-muted-foreground">Record who paid now — they get reimbursed from the fund right away.</span>
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRecordPayment?.(session)}
              data-testid={`session-record-payment-${session.id}`}
              className="rounded-full text-xs shrink-0 border-sky-500/50 text-sky-700 hover:bg-sky-500/10"
            >
              Record
            </Button>
          </div>
          )
        )}
        {/* iter275 — fallback for legacy sessions swept to 'not_needed': quiet
             ghost link so admins can still record a venue payer. */}
        {isAdmin && session.payment_status === 'not_needed' && !session.payment && !isCancelled && (
          <button
            type="button"
            onClick={() => onRecordPayment?.(session)}
            data-testid={`session-record-payment-fallback-${session.id}`}
            className="mt-2 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 decoration-dotted inline-flex items-center gap-1"
          >
            <Wallet className="w-3 h-3" /> Record venue payer
          </button>
        )}
        {/* iter227 — Recorded state (admin can undo) */}
        {isAdmin && session.payment_status === 'recorded' && session.payment && (
          <div
            className="mt-3 flex items-center justify-between gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-2.5"
            data-testid={`session-payment-recorded-${session.id}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Wallet className="w-4 h-4 text-emerald-600 shrink-0" />
              <p className="text-xs sm:text-sm text-emerald-800 dark:text-emerald-300 truncate">
                {session.payment.paid_by === 'complimentary' ? (
                  <>🎁 <span className="font-semibold">Complimentary session</span> — no venue cost, session fees waived</>
                ) : (
                  <>
                    Paid by <span className="font-semibold">{session.payment.paid_by_name}</span>
                    {session.payment.amount ? ` · ₹${Math.round(session.payment.amount).toLocaleString('en-IN')}` : ''}
                    {session.payment.paid_by !== 'group_fund' && ' · reimbursed from fund'}
                  </>
                )}
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onUndoPayment?.(session.id)}
              data-testid={`session-undo-payment-${session.id}`}
              className="rounded-full text-xs text-red-500 hover:text-red-600 shrink-0"
            >
              Undo
            </Button>
          </div>
        )}
        {/* iter175 — Session notes + court/venue info panel (visible to all members) */}
        {(session.notes || session.court_info || session.location_url) && (
          <div
            className="mt-3 pt-3 border-t border-border space-y-2"
            data-testid={`session-meta-${session.id}`}
          >
            {(session.court_info || session.location_url) && (
              <div className="flex items-start gap-2 text-xs">
                <MapPin className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  {session.court_info && (
                    <p className="font-semibold text-foreground break-words" data-testid={`session-court-${session.id}`}>
                      {session.court_info}
                    </p>
                  )}
                  {session.location_url && (
                    <a
                      href={session.location_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`session-location-link-${session.id}`}
                      className="inline-flex items-center gap-1 text-primary hover:underline text-xs mt-0.5"
                    >
                      Open in Maps
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            )}
            {session.notes && (
              <div className="flex items-start gap-2 text-xs" data-testid={`session-notes-${session.id}`}>
                <StickyNote className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                <p className="text-foreground/80 leading-relaxed whitespace-pre-wrap break-words flex-1">
                  {session.notes}
                </p>
              </div>
            )}
          </div>
        )}

        {/* RSVP block */}
        {isRsvp && !isCancelled && session.status !== 'completed' && (
          <div className="mt-3 pt-3 border-t border-border space-y-2" data-testid={`rsvp-block-${session.id}`}>
            <div className="flex items-center justify-between text-xs flex-wrap gap-2">
              <span className="font-semibold">
                RSVP {isOpenForRsvp ? 'open' : 'closed'} · {totalHeadcount}/{session.max_players} confirmed
                {guestCount > 0 && ` (${guestCount} guest${guestCount === 1 ? '' : 's'})`}
                {session.waitlist_ids?.length > 0 && ` · waitlist ${session.waitlist_ids.length}`}
              </span>
              {session.rsvp_closes_at && isOpenForRsvp && (
                <span className="text-muted-foreground">
                  Closes {new Date(session.rsvp_closes_at).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}
                </span>
              )}
            </div>

            {/* RSVP buttons — moved from original layout below */}
            <RsvpButtons
              session={session}
              isAdmin={isAdmin}
              isOpenForRsvp={isOpenForRsvp}
              displayStatus={displayStatus}
              handleRsvp={handleRsvp}
              handleLockRsvp={handleLockRsvp}
              openAdminRsvp={openAdminRsvp}
              handleAdminRsvpRemove={handleAdminRsvpRemove}
              handleAddGuest={handleAddGuest}
              handleRemoveGuest={handleRemoveGuest}
              handleCancelSession={handleCancelSession}
              silentGuestMatchByGuestId={silentGuestMatchByGuestId}
              handleConvertSilentGuest={handleConvertSilentGuest}
              convertingGuestId={convertingGuestId}
            />
          </div>
        )}
      </Card>
    </SessionCardDecoration>
    </div>
  );
}

/** Confirmed / guest / waitlist chips + RSVP buttons + admin controls.
 *  Extracted from SessionRow so the row stays readable. */
function RsvpButtons({
  session, isAdmin, isOpenForRsvp, displayStatus,
  handleRsvp, handleLockRsvp, openAdminRsvp, handleAdminRsvpRemove, handleAddGuest, handleRemoveGuest, handleCancelSession,
  silentGuestMatchByGuestId, handleConvertSilentGuest, convertingGuestId,
}) {
  void displayStatus;
  const guestCount = session.external_guests?.length || 0;
  const totalHead = (session.confirmed_ids?.length || 0) + guestCount;
  const needsGuests = session.status === 'needs_guests' && totalHead < (session.min_players || 0);
  const isCancelled = session.status === 'cancelled';
  const canAdminRemove = isAdmin && !isCancelled && session.status !== 'completed';
  return (
    <>
      {(session.confirmed_names?.length > 0 || guestCount > 0 || session.waitlist_names?.length > 0) && (
        <div className="flex flex-wrap gap-1" data-testid={`rsvp-roster-${session.id}`}>
          {session.confirmed_names?.map((name, i) => {
            const at = formatRsvpAt(session.confirmed_rsvp_ats?.[i]);
            const uid = session.confirmed_ids?.[i];
            const chipTitle = at ? `In #${i + 1} at ${at}` : `In #${i + 1}`;
            return (
              <span
                key={`c${i}`}
                className="text-xs pl-2 pr-1 py-0.5 rounded-full bg-green-500/15 text-green-700 font-medium inline-flex items-center gap-1"
                title={chipTitle}
                data-testid={`confirmed-chip-${session.id}-${i}`}
              >
                <span className="font-bold text-xs text-green-600/70">#{i + 1}</span>
                {name}
                {at && (
                  <span className="text-xs tabular-nums text-green-600/70">· {at}</span>
                )}
                {canAdminRemove && uid && handleAdminRsvpRemove && (
                  <button
                    type="button"
                    onClick={() => handleAdminRsvpRemove(session.id, uid, name)}
                    className="ml-0.5 -mr-0.5 hover:bg-red-500/20 hover:text-red-700 rounded-full p-0.5 transition-colors"
                    title={`Admin: remove ${name} (no fine, auto-promote waitlist)`}
                    data-testid={`admin-remove-confirmed-${session.id}-${i}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            );
          })}
          {session.external_guests?.map((g) => {
            const silentMatch = g.silent_add ? (silentGuestMatchByGuestId || {})[g.id] : null;
            // iter207 — Show name + last-4 of phone together on the chip so
            // admins can identify the guest even when a name wasn't entered.
            // If the saved name is the server fallback "Guest <last4>", we
            // just show that (no redundant phone suffix). Otherwise we show
            // "<Name> · 9111" so both pieces of info are visible.
            const phoneSuffix = (g.phone || '').replace(/\D/g, '').slice(-4);
            const isFallbackName = phoneSuffix && g.name === `Guest ${phoneSuffix}`;
            const chipLabel = !phoneSuffix || isFallbackName
              ? g.name
              : `${g.name} · ${phoneSuffix}`;
            return (
              <span
                key={g.id}
                className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${
                  silentMatch
                    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-2 ring-emerald-500/30'
                    : 'bg-blue-500/15 text-blue-700'
                }`}
                data-testid={`guest-chip-${g.id}`}
                title={g.phone ? `${g.name} · ${g.phone}` : g.name}
              >
                {chipLabel} {g.silent_add ? '(awaiting signup)' : '(guest)'}
                {silentMatch && isAdmin && (
                  <button
                    type="button"
                    onClick={() => handleConvertSilentGuest(session.id, silentMatch)}
                    disabled={convertingGuestId === g.id}
                    className="ml-0.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500/25 hover:bg-emerald-500/40 text-emerald-800 dark:text-emerald-100 text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
                    title={`${silentMatch.matched_user.name} signed up — tap to link their visits to their account`}
                    data-testid={`convert-silent-guest-${g.id}`}
                  >
                    {convertingGuestId === g.id ? (
                      <Loader2 className="w-2.5 h-2.5 animate-spin" />
                    ) : (
                      <>
                        <Sparkles className="w-2.5 h-2.5" />
                        Link
                      </>
                    )}
                  </button>
                )}
                {isAdmin && !silentMatch && (
                  <button onClick={() => handleRemoveGuest(session.id, g.id)} className="hover:text-red-500" title="Remove">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            );
          })}
          {session.waitlist_names?.map((name, i) => {
            const at = formatRsvpAt(session.waitlist_rsvp_ats?.[i]);
            const uid = session.waitlist_ids?.[i];
            const chipTitle = at ? `Waitlist #${i + 1} at ${at}` : `Waitlist #${i + 1}`;
            return (
              <span
                key={`w${i}`}
                className="text-xs pl-2 pr-1 py-0.5 rounded-full bg-amber-500/15 text-amber-700 font-medium inline-flex items-center gap-1"
                title={chipTitle}
                data-testid={`waitlist-chip-${session.id}-${i}`}
              >
                ⏳ {name}
                {at && (
                  <span className="text-xs tabular-nums text-amber-600/70">· {at}</span>
                )}
                {canAdminRemove && uid && handleAdminRsvpRemove && (
                  <button
                    type="button"
                    onClick={() => handleAdminRsvpRemove(session.id, uid, name)}
                    className="ml-0.5 -mr-0.5 hover:bg-red-500/20 hover:text-red-700 rounded-full p-0.5 transition-colors"
                    title={`Admin: remove ${name} from waitlist`}
                    data-testid={`admin-remove-waitlist-${session.id}-${i}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {needsGuests && (
        <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid={`needs-guests-${session.id}`}>
          <p className="text-xs font-semibold text-amber-700">Short of minimum ({totalHead}/{session.min_players}). Add guests, or go ahead anyway — you can still mark attendance and charge.</p>
        </div>
      )}

      {/* Member RSVP buttons */}
      {(isOpenForRsvp || (session.rsvp_locked && !isCancelled && session.status !== 'completed' && (session.my_rsvp === 'confirmed' || session.my_rsvp === 'waitlist'))) && (
        <div className="flex gap-2">
          {isOpenForRsvp && session.my_rsvp !== 'confirmed' && session.my_rsvp !== 'waitlist' && (
            <Button size="sm" onClick={() => handleRsvp(session.id, 'in', session)} data-testid={`rsvp-in-${session.id}`} className="rounded-full text-xs flex-1">
              <UserCheck className="w-3.5 h-3.5 mr-1" /> I&apos;m IN
            </Button>
          )}
          {(session.my_rsvp === 'confirmed' || session.my_rsvp === 'waitlist') && (
            <Button size="sm" variant="outline" onClick={() => handleRsvp(session.id, 'out', session)} data-testid={`rsvp-out-${session.id}`} className="rounded-full text-xs flex-1">
              <UserX className="w-3.5 h-3.5 mr-1" /> Drop OUT
              {session.my_rsvp === 'waitlist' && ' (waitlist)'}
              {session.rsvp_locked && session.my_rsvp === 'confirmed' && ' ⚠️'}
            </Button>
          )}
        </div>
      )}

      {/* Admin controls */}
      {isAdmin && !session.rsvp_locked && (
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => handleLockRsvp(session.id)} data-testid={`lock-rsvp-${session.id}`} className="rounded-full text-xs">
            Lock RSVP
          </Button>
          <Button size="sm" variant="outline" onClick={() => openAdminRsvp(session)} data-testid={`add-member-${session.id}`} className="rounded-full text-xs">
            <UserPlus className="w-3.5 h-3.5 mr-1" /> Add Member
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleAddGuest(session.id)} data-testid={`add-guest-${session.id}`} className="rounded-full text-xs">
            <UserPlus className="w-3.5 h-3.5 mr-1" /> Add Guest
          </Button>
          <Button size="sm" variant="ghost" onClick={() => handleCancelSession(session.id)} data-testid={`cancel-session-${session.id}`} className="rounded-full text-xs text-red-500">
            Cancel Session
          </Button>
        </div>
      )}
      {isAdmin && session.rsvp_locked && !isCancelled && session.status !== 'completed' && (
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => openAdminRsvp(session)} data-testid={`add-member-${session.id}`} className="rounded-full text-xs">
            <UserPlus className="w-3.5 h-3.5 mr-1" /> Add Member
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleAddGuest(session.id)} data-testid={`add-guest-${session.id}`} className="rounded-full text-xs">
            <UserPlus className="w-3.5 h-3.5 mr-1" /> Add Guest
          </Button>
          <Button
            size="sm"
            variant={needsGuests ? "destructive" : "ghost"}
            onClick={() => handleCancelSession(session.id)}
            data-testid={`cancel-session-${session.id}`}
            className={`rounded-full text-xs ${needsGuests ? '' : 'text-red-500'}`}
          >
            Cancel Session
          </Button>
        </div>
      )}
    </>
  );
}


/**
 * SessionRecoveryKebab — iter197.5, refreshed in iter198.1 with a bottom-sheet.
 * Tiny ⋯ kebab on every COMPLETED + FEES-CHARGED session card. Tapping it
 * slides up a full-width bottom sheet with three admin actions:
 *   • Edit per-attendee fee   (opens EditAttendeeFeesDialog)
 *   • Reverse all session fees (full refund)         ← DANGER ZONE
 *   • Re-open session         (clears attendance)    ← DANGER ZONE
 *
 * The bottom-sheet pattern (vs. the earlier absolute-positioned popover)
 * eliminates the overlap with neighbouring session cards and matches the
 * GuestLinkSheet / PayByPhoneSheet pattern used elsewhere in the app.
 */
function SessionRecoveryKebab({ session, isBusy, onEditFees, onEditFines, onReverseFees, onReopenSession }) {
  const [open, setOpen] = useState(false);

  const fire = (fn) => {
    setOpen(false);
    // Defer one tick so the sheet's close animation can run without
    // racing the dialog mount that follows (e.g., EditAttendeeFeesDialog).
    setTimeout(() => fn?.(), 80);
  };

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        disabled={isBusy}
        data-testid={`session-recovery-kebab-${session.id}`}
        className="rounded-full text-xs text-muted-foreground hover:text-primary h-7 w-7 p-0"
        title="Recovery options"
      >
        {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MoreVertical className="w-3.5 h-3.5" />}
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="rounded-t-3xl border-t-2 px-0 pt-4 pb-8 max-h-[80vh] overflow-y-auto"
          data-testid={`session-recovery-menu-${session.id}`}
        >
          {/* drag handle */}
          <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-muted-foreground/25" />

          <SheetHeader className="px-5 text-left">
            <SheetTitle className="font-display text-lg font-bold tracking-tight">
              Session actions
            </SheetTitle>
            <SheetDescription className="text-xs text-muted-foreground">
              {session.title || session.name || `Session ${(session.id || '').slice(0, 6)}`}
            </SheetDescription>
          </SheetHeader>

          {/* SAFE actions */}
          {!session.fees_charged && (
            <div className="mt-5 mx-3 rounded-2xl bg-sky-500/10 border border-sky-500/30 px-3 py-2.5 text-[12px] text-sky-800 dark:text-sky-200" data-testid={`session-recovery-hint-${session.id}`}>
              Marked someone wrong? Fees aren't charged yet, so just <b>Re-open</b> below and re-mark attendance — nothing to refund.
            </div>
          )}
          <div className={`mt-5 px-3 ${session.fees_charged ? '' : 'hidden'}`}>
            <button
              type="button"
              onClick={() => fire(onEditFees)}
              className="w-full flex items-center gap-3 rounded-2xl px-3 py-3.5 hover:bg-muted/60 active:bg-muted transition-colors text-left"
              data-testid={`session-recovery-edit-fees-${session.id}`}
            >
              <span className="shrink-0 w-10 h-10 rounded-xl bg-primary/12 text-primary flex items-center justify-center">
                <Edit3 className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold">Edit per-attendee fee</span>
                <span className="block text-xs text-muted-foreground leading-snug mt-0.5">
                  Tweak one member&apos;s amount without touching the rest
                </span>
              </span>
            </button>
            {/* iter206 — Edit fines. Mirrors per-attendee fee editing but for
                auto-issued late_cancel / no_show / late-arrival fines. */}
            <button
              type="button"
              onClick={() => fire(onEditFines)}
              className="w-full flex items-center gap-3 rounded-2xl px-3 py-3.5 hover:bg-amber-500/8 active:bg-amber-500/12 transition-colors text-left"
              data-testid={`session-recovery-edit-fines-${session.id}`}
            >
              <span className="shrink-0 w-10 h-10 rounded-xl bg-amber-500/15 text-amber-700 dark:text-amber-300 flex items-center justify-center">
                <ShieldAlert className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold">Edit fines</span>
                <span className="block text-xs text-muted-foreground leading-snug mt-0.5">
                  Adjust or waive late-cancel / no-show fines · refunds the member instantly
                </span>
              </span>
            </button>
          </div>

          {/* DANGER ZONE */}
          <div className="mt-5 px-5">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-red-600/80 dark:text-red-400/80 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" />
              Danger zone
            </p>
          </div>
          <div className="mt-2 px-3 space-y-1">
            {session.fees_charged && <button
              type="button"
              onClick={() => fire(onReverseFees)}
              className="w-full flex items-center gap-3 rounded-2xl px-3 py-3.5 hover:bg-amber-500/10 active:bg-amber-500/15 transition-colors text-left"
              data-testid={`session-recovery-reverse-fees-${session.id}`}
            >
              <span className="shrink-0 w-10 h-10 rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-300 flex items-center justify-center">
                <CoinsIcon className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-amber-700 dark:text-amber-300">Reverse all fees</span>
                <span className="block text-xs text-muted-foreground leading-snug mt-0.5">
                  Refund every attendee and reset the session to unchecked-fees
                </span>
              </span>
            </button>}
            <button
              type="button"
              onClick={() => fire(onReopenSession)}
              className="w-full flex items-center gap-3 rounded-2xl px-3 py-3.5 hover:bg-red-500/10 active:bg-red-500/15 transition-colors text-left"
              data-testid={`session-recovery-reopen-${session.id}`}
            >
              <span className="shrink-0 w-10 h-10 rounded-xl bg-red-500/15 text-red-600 dark:text-red-300 flex items-center justify-center">
                <RotateCcw className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-red-700 dark:text-red-300">Re-open session</span>
                <span className="block text-xs text-muted-foreground leading-snug mt-0.5">
                  Clear attendance &amp; fees so you can re-mark from scratch
                </span>
              </span>
            </button>
          </div>

          {/* Cancel */}
          <div className="mt-4 px-5">
            <Button
              variant="outline"
              className="w-full rounded-full"
              onClick={() => setOpen(false)}
              data-testid={`session-recovery-cancel-${session.id}`}
            >
              Cancel
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
